import React, { RefObject } from 'react';
import type { FieldComponentProps } from 'react-modular-forms';
import { config, registerType } from 'react-modular-forms';
import {
    CheckboxDropdownFields,
    CheckboxDropdownFieldsProps
} from '../CheckboxDropdownFields/CheckboxDropdownFields';
import { Editor, EditorProps } from '../Editor/Editor';
import { TimeCode } from '../TimeCode/TimeCode';
import { ColorPicker } from './components/ColorPicker/ColorPicker';
import { DatePicker } from './components/DatePicker/DatePicker';
import { EmailPicker } from './components/EmailPicker/EmailPicker';
import { WorkspaceMemberPicker } from './components/EmailPicker/WorkspaceMemberPicker';
import { FormRecaptcha } from './components/FormRecaptcha';
import { GraphicPicker } from './components/GraphicPicker/GraphicPicker';
import { ItemPicker } from './components/ItemPicker/ItemPicker';
import { RadioToggle } from './components/RadioTab/RadioToggle';
import { ReactInputMask } from './components/ReactInputMask/ReactInputMask';
import { ReactSelect } from './components/ReactSelect/ReactSelect';
import { RichNumber } from './components/RichNumber/RichNumber';
import { UserPicker } from './components/UserPicker/UserPicker';

config.fieldClassName = 'form-field';
config.errorClassName = 'form-field-error';
config.sendEmptyStringsAs = undefined;

export const configureReactModularForms = () => {
    registerType('checkbox-dropdown', {
        Component: ({
            options,
            theme,
            formId,
            buttonLabel,
            direction,
            onChange,
            group,
            name,
            groupMin,
            value
        }: FieldComponentProps & CheckboxDropdownFieldsProps) => {
            return (
                <CheckboxDropdownFields
                    formId={formId}
                    name={name}
                    options={options}
                    theme={theme}
                    buttonLabel={buttonLabel}
                    direction={direction}
                    onChange={onChange}
                    group={group}
                    groupMin={groupMin}
                    value={value}
                />
            );
        },
        isStatic: true
    });

    registerType('date-picker', {
        Component: DatePicker,
        getValue: (ref) => {
            return ref.current;
        }
    });

    registerType('color-picker', {
        Component: ColorPicker,
        isStatic: true
    });

    registerType('workspace-member-picker', {
        Component: WorkspaceMemberPicker,
        isStatic: true
    });

    registerType('email-picker', {
        Component: EmailPicker,
        isStatic: true
    });

    registerType('graphic-picker', {
        Component: GraphicPicker,
        isStatic: true
    });

    registerType('item-picker', {
        Component: ItemPicker,
        isStatic: true
    });

    registerType('masked-field', {
        Component: ReactInputMask,
        labelBefore: true,
        getValue: (ref: RefObject<HTMLInputElement>) => {
            return ref.current?.value;
        }
    });

    registerType('radio-toggle', {
        Component: RadioToggle,
        isStatic: true
    });

    registerType('react-select', {
        Component: ReactSelect,
        getValue: (ref: RefObject<string | undefined>) => ref.current
    });

    registerType('recaptcha', {
        Component: FormRecaptcha,
        isStatic: true
    });

    registerType('rich-number', {
        Component: RichNumber,
        getValue: (ref: RefObject<HTMLInputElement>) => ref.current?.value
    });

    registerType('rich-text', {
        Component: ({ componentRef, ...sharedProps }: FieldComponentProps & EditorProps) => {
            return <Editor ref={componentRef} {...sharedProps} />;
        },
        getValue: (ref) => {
            // Please, do not use the tiptap Editor.getHTML() function:
            // it does not return the correct HTML and does not work in Safari.
            const editor = ref.current as HTMLDivElement;
            return editor.children[0]?.innerHTML || null;
        }
    });

    registerType('timecode', {
        Component: TimeCode,
        isStatic: true
    });

    registerType('user-picker', {
        Component: UserPicker,
        isStatic: true
    });
};
